import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useTranslations } from "../hooks/useTranslations";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { SEOHead } from "./SEOHead";
import blogLandingContent from "../content/blog/blogLanding.json";
import type { SupportedLanguage } from "../locales";
import {
  getAllArticles,
  getArticleBySlug,
  getBlogArticlePath,
} from "../blog/articles";
import {
  getBasePathFromUrlPath,
  getLocalizedPath,
  getBlogArticleSlugFromUrlPath,
  type LocalizedBasePath,
} from "../utils/localizedRoutes";

const BLOG_HERO_IMAGE = "/blog-images/vevi-ai-blog-hero.jpg";
const ARTICLE_TITLE_CLASS_NAME = "mb-4 max-w-5xl font-semibold tracking-tight text-white";
const ARTICLE_TITLE_STYLE = { fontSize: "48px", lineHeight: "0.98" } as const;
const ARTICLE_DESCRIPTION_CLASS_NAME =
  "max-w-4xl text-base leading-relaxed text-purple-100 sm:text-lg";
const INLINE_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const INLINE_LINK_STYLE = {
  color: "#6d28d9",
  fontWeight: 600,
  textDecorationLine: "underline",
  textDecorationColor: "#c4b5fd",
  textUnderlineOffset: "2px",
} as const;

type BlogLandingCopy = {
  title: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
};

type MoreArticlesCopy = {
  title: string;
  description: string;
};

const blogLandingCopyByLanguage = blogLandingContent as Record<string, BlogLandingCopy>;
const moreArticlesCopyByLanguage: Record<SupportedLanguage, MoreArticlesCopy> = {
  en: {
    title: "One More Article Won’t Hurt",
    description: "Fresh articles Vevi AI Dubbing Monster dragged out of the internet cave",
  },
  ru: {
    title: "Еще одна статья не повредит",
    description: "Свежие статьи, которые Vevi AI Dubbing Monster вытащил из интернет-пещеры",
  },
  es: {
    title: "Un artículo más no hace daño",
    description: "Artículos frescos que Vevi AI Dubbing Monster sacó de la cueva de internet",
  },
  fr: {
    title: "Un article de plus ne fera pas de mal",
    description: "Des articles tout frais que Vevi AI Dubbing Monster a sortis de la grotte d’Internet",
  },
  de: {
    title: "Ein weiterer Artikel schadet nicht",
    description: "Frische Artikel, die Vevi AI Dubbing Monster aus der Internet-Höhle gezogen hat",
  },
  it: {
    title: "Un altro articolo non fa male",
    description: "Articoli freschi che Vevi AI Dubbing Monster ha tirato fuori dalla caverna di Internet",
  },
  pt: {
    title: "Mais um artigo não faz mal",
    description: "Artigos fresquinhos que o Vevi AI Dubbing Monster tirou da caverna da internet",
  },
  pl: {
    title: "Jeszcze jeden artykuł nie zaszkodzi",
    description: "Świeże artykuły, które Vevi AI Dubbing Monster wyciągnął z internetowej jaskini",
  },
  zh: {
    title: "再来一篇文章也无妨",
    description: "Vevi AI Dubbing Monster 从互联网洞穴里拖出来的新鲜文章",
  },
  ja: {
    title: "もう1本読んでも損はない",
    description: "Vevi AI Dubbing Monsterがネットの洞窟から引っ張り出してきた新着記事",
  },
  ko: {
    title: "기사 하나쯤 더 읽어도 괜찮죠",
    description: "Vevi AI Dubbing Monster가 인터넷 동굴에서 끌어낸 따끈한 새 글",
  },
  ar: {
    title: "مقال آخر لن يضر",
    description: "مقالات جديدة سحبها Vevi AI Dubbing Monster من كهف الإنترنت",
  },
  bg: {
    title: "Още една статия няма да навреди",
    description: "Свежи статии, които Vevi AI Dubbing Monster измъкна от интернет пещерата",
  },
  hr: {
    title: "Još jedan članak neće škoditi",
    description: "Svježi članci koje je Vevi AI Dubbing Monster izvukao iz internetske špilje",
  },
  cs: {
    title: "Ještě jeden článek neuškodí",
    description: "Čerstvé články, které Vevi AI Dubbing Monster vytáhl z internetové jeskyně",
  },
  da: {
    title: "En artikel mere skader ikke",
    description: "Friske artikler, som Vevi AI Dubbing Monster har trukket ud af internettets hule",
  },
  nl: {
    title: "Nog een artikel kan geen kwaad",
    description: "Verse artikelen die Vevi AI Dubbing Monster uit de internetgrot heeft gesleept",
  },
  et: {
    title: "Veel üks artikkel ei tee paha",
    description: "Värsked artiklid, mille Vevi AI Dubbing Monster internetikoopast välja tiris",
  },
  fi: {
    title: "Yksi artikkeli lisää ei haittaa",
    description: "Tuoreita artikkeleita, jotka Vevi AI Dubbing Monster raahasi ulos internetluolasta",
  },
  el: {
    title: "Ακόμα ένα άρθρο δεν θα βλάψει",
    description: "Φρέσκα άρθρα που το Vevi AI Dubbing Monster έσυρε έξω από τη σπηλιά του ίντερνετ",
  },
  hu: {
    title: "Még egy cikk nem árthat",
    description: "Friss cikkek, amelyeket a Vevi AI Dubbing Monster rángatott elő az internet barlangjából",
  },
  ga: {
    title: "Ní dhéanfaidh alt amháin eile dochar",
    description: "Ailt úra a tharraing Vevi AI Dubbing Monster amach as uaimh an idirlín",
  },
  lv: {
    title: "Vēl viens raksts nenāks par ļaunu",
    description: "Svaigi raksti, ko Vevi AI Dubbing Monster izvilka no interneta alas",
  },
  lt: {
    title: "Dar vienas straipsnis nepakenks",
    description: "Švieži straipsniai, kuriuos Vevi AI Dubbing Monster ištempė iš interneto olos",
  },
  mt: {
    title: "Artiklu ieħor ma jagħmilx ħsara",
    description: "Artikli friski li Vevi AI Dubbing Monster ġibed barra mill-grotta tal-internet",
  },
  ro: {
    title: "Încă un articol nu strică",
    description: "Articole proaspete pe care Vevi AI Dubbing Monster le-a scos din peștera internetului",
  },
  sk: {
    title: "Ešte jeden článok neuškodí",
    description: "Čerstvé články, ktoré Vevi AI Dubbing Monster vytiahol z internetovej jaskyne",
  },
  sl: {
    title: "Še en članek ne škodi",
    description: "Sveži članki, ki jih je Vevi AI Dubbing Monster potegnil iz internetne jame",
  },
  sv: {
    title: "En artikel till skadar inte",
    description: "Färska artiklar som Vevi AI Dubbing Monster drog fram ur internets grotta",
  },
};

export function BlogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLanguage } = useTranslations();
  const blog = t.blog;
  const backToHomeByLanguage: Record<string, string> = {
    en: "Back to home",
    ru: "На главную",
    es: "Volver al inicio",
    fr: "Retour à l'accueil",
    de: "Zurück zur Startseite",
    it: "Torna alla home",
    pt: "Voltar à página inicial",
    pl: "Wróć do strony głównej",
    ja: "ホームに戻る",
    ko: "홈으로 돌아가기",
    zh: "返回首页",
    ar: "العودة إلى الرئيسية",
    bg: "Обратно към началната страница",
    hr: "Povratak na početnu",
    cs: "Zpět na hlavní stránku",
    da: "Tilbage til forsiden",
    nl: "Terug naar home",
    et: "Tagasi avalehele",
    fi: "Takaisin etusivulle",
    el: "Επιστροφή στην αρχική",
    hu: "Vissza a főoldalra",
    ga: "Ar ais go dtí an baile",
    lv: "Atpakaļ uz sākumlapu",
    lt: "Grįžti į pradžios puslapį",
    mt: "Lura għall-paġna ewlenija",
    ro: "Înapoi la pagina principală",
    sk: "Späť na hlavnú stránku",
    sl: "Nazaj na domačo stran",
    sv: "Tillbaka till startsidan",
  };
  const backToHomeLabel = backToHomeByLanguage[currentLanguage] || backToHomeByLanguage.en;
  const articleSlug = getBlogArticleSlugFromUrlPath(location.pathname);
  const selectedArticle = articleSlug ? getArticleBySlug(articleSlug, currentLanguage) : null;
  const articles = getAllArticles(currentLanguage);
  const moreArticles = selectedArticle
    ? articles.filter((article) => article.id !== selectedArticle.id).slice(0, 3)
    : [];
  const blogLandingCopy =
    blogLandingCopyByLanguage[currentLanguage] || blogLandingCopyByLanguage.en;
  const blogLandingTitle = blogLandingCopy.title || blog.title;
  const blogLandingDescription = blogLandingCopy.description || blog.subtitle;
  const blogLandingSeoTitle = blogLandingCopy.seoTitle || `${blogLandingTitle} | VEVI Monster`;
  const blogLandingSeoDescription = blogLandingCopy.seoDescription || blogLandingDescription;
  const moreArticlesCopy = moreArticlesCopyByLanguage[currentLanguage] || moreArticlesCopyByLanguage.en;

  const buildUrl = (path: LocalizedBasePath) => getLocalizedPath(path, currentLanguage);
  const homeUrl = buildUrl("/");
  const blogUrl = buildUrl("/blog");

  const resolveInlineHref = (href: string): string => {
    if (!href.startsWith("/")) {
      return href;
    }

    const linkedArticleSlug = getBlogArticleSlugFromUrlPath(href);
    if (linkedArticleSlug) {
      const linkedArticle = getArticleBySlug(linkedArticleSlug, currentLanguage);
      if (linkedArticle) {
        return getBlogArticlePath(linkedArticle, currentLanguage);
      }
    }

    const basePath = getBasePathFromUrlPath(href);
    return basePath ? getLocalizedPath(basePath, currentLanguage) : href;
  };

  const renderInlineText = (text: string, keyPrefix: string): React.ReactNode[] => {
    const matches = Array.from(text.matchAll(INLINE_LINK_RE));

    if (matches.length === 0) {
      return [text];
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
      const [fullMatch, label, href] = match;
      const matchIndex = match.index ?? 0;

      if (matchIndex > lastIndex) {
        parts.push(text.slice(lastIndex, matchIndex));
      }

      const resolvedHref = resolveInlineHref(href.trim());
      const linkKey = `${keyPrefix}-link-${index}`;
      const linkClassName =
        "transition-colors hover:text-purple-800";

      if (resolvedHref.startsWith("/")) {
        parts.push(
          <Link
            key={linkKey}
            to={resolvedHref}
            className={linkClassName}
            style={INLINE_LINK_STYLE}
          >
            {label}
          </Link>,
        );
      } else {
        parts.push(
          <a
            key={linkKey}
            href={resolvedHref}
            className={linkClassName}
            style={INLINE_LINK_STYLE}
            target="_blank"
            rel="noreferrer"
          >
            {label}
          </a>,
        );
      }

      lastIndex = matchIndex + fullMatch.length;
    });

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const formatText = (text: string, keyPrefix: string): React.ReactNode => {
    const lines = text.split("\n").filter((line) => line.trim());

    if (lines.length > 1) {
      return (
        <>
          {lines.map((line, idx) => (
            <React.Fragment key={`${keyPrefix}-line-${idx}`}>
              {renderInlineText(line.trim(), `${keyPrefix}-line-${idx}`)}
              {idx < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </>
      );
    }

    return renderInlineText(text, keyPrefix);
  };

  const isHeading = (text: string): boolean => {
    if (text.match(/^[🌑👾📌🔹]/)) {
      return true;
    }
    if (text.length < 80 && text.endsWith(":")) {
      return true;
    }
    return false;
  };

  const renderBlogHeroSection = ({
    title,
    description,
  }: {
    title: string;
    description?: string;
  }) => (
    <>
      <div className="mb-6 overflow-hidden rounded-xl border border-white/15 bg-slate-950/20 shadow-xl">
        <div className="relative">
          <img
            src={BLOG_HERO_IMAGE}
            alt="VEVI Monster blog hero"
            className="block h-auto w-full"
            style={{ objectPosition: "center center" }}
          />
        </div>
      </div>

      <div className="mb-6">
        <div className="mb-4 flex flex-wrap items-center gap-3 text-sm font-medium text-white">
          <Link
            to={homeUrl}
            className="inline-flex items-center transition-colors hover:text-purple-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span>{backToHomeLabel}</span>
          </Link>
          <span className="text-purple-200/80">|</span>
          <Link
            to={blogUrl}
            className="transition-colors hover:text-purple-100"
          >
            Blog
          </Link>
        </div>

        <h1
          className={ARTICLE_TITLE_CLASS_NAME}
          style={ARTICLE_TITLE_STYLE}
        >
          {title}
        </h1>

        {description && (
          <p className={ARTICLE_DESCRIPTION_CLASS_NAME}>
            {description}
          </p>
        )}
      </div>
    </>
  );

  if (articleSlug && !selectedArticle) {
    return (
      <>
        <SEOHead
          overrideSeo={{
            title: `${blog.articleNotFound} | VEVI Monster`,
            description: blog.articleNotFound,
            keywords: "vevi monster blog",
          }}
          canonicalPathOverride={buildUrl("/blog")}
          canonicalPathIsLocalized={true}
        />
        <Header />
        <div className="min-h-screen vevi-site-background" style={{ paddingTop: "140px" }}>
          <div className="vevi-page-grid">
            <div className="vevi-page-main py-12">
              {renderBlogHeroSection({
                title: blog.articleNotFound,
                description: blogLandingDescription,
              })}

              <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
                <CardContent className="p-8 text-center">
                  <h2 className="text-gray-900 text-2xl mb-4">{blog.articleNotFound}</h2>
                  <button
                    onClick={() => navigate(blogUrl)}
                    className="text-purple-600 hover:text-purple-700"
                  >
                    {blog.backToBlog}
                  </button>
                </CardContent>
              </Card>
            </div>
          </div>
          <Footer onShowBlog={() => navigate(buildUrl("/blog"))} />
        </div>
      </>
    );
  }

  if (selectedArticle) {
    return (
      <>
        <SEOHead
          overrideSeo={{
            title: selectedArticle.seoTitle,
            description: selectedArticle.seoDescription,
            keywords: `vevi monster blog, ${selectedArticle.slug.replace(/-/g, ", ")}`,
          }}
          canonicalPathOverride={getBlogArticlePath(selectedArticle, currentLanguage)}
          canonicalPathIsLocalized={true}
        />
        <Header />
        <div className="min-h-screen vevi-site-background" style={{ paddingTop: "140px" }}>
          <div className="vevi-page-grid">
            <div className="vevi-page-main" style={{ paddingBottom: "72px" }}>
              {renderBlogHeroSection({
                title: selectedArticle.title,
                description: selectedArticle.excerpt,
              })}

              <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
                <CardContent className="p-8 space-y-6">
                  {selectedArticle.coverImage && (
                    <div className="overflow-hidden rounded-xl border border-gray-200">
                      <img
                        src={selectedArticle.coverImage}
                        alt={selectedArticle.title}
                        className="w-full max-h-[520px] object-cover"
                        loading="eager"
                      />
                    </div>
                  )}

                  {selectedArticle.metadata && Object.keys(selectedArticle.metadata).length > 0 && (
                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                      <h2 className="text-gray-800 text-lg font-semibold mb-4">{blog.characteristics}</h2>
                      <div className="space-y-2">
                        {Object.entries(selectedArticle.metadata).map(([key, value]) => (
                          <div key={key} className="flex">
                            <strong className="text-gray-800 min-w-[200px]">{key}:</strong>
                            <span className="text-gray-700">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {selectedArticle.content.map((paragraph, index) => {
                      const text = paragraph.trim();

                      if (!text) {
                        return null;
                      }

                      const isHeadingText = isHeading(text);
                      const isKeyValue = text.match(/^[^:]+:\s/);

                      if (isHeadingText) {
                        return (
                          <h2 key={index} className="text-gray-900 text-2xl mb-4 mt-6 font-semibold">
                            {text}
                          </h2>
                        );
                      }

                      if (isKeyValue) {
                        const [key, ...valueParts] = text.split(":");
                        const value = valueParts.join(":").trim();
                        return (
                          <div key={index} className="mb-2">
                            <strong className="text-gray-800">{key}:</strong>{" "}
                            <span className="text-gray-700">
                              {formatText(value, `article-metadata-${index}`)}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <p key={index} className="text-gray-700 leading-relaxed">
                          {formatText(text, `article-paragraph-${index}`)}
                        </p>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {moreArticles.length > 0 && (
                <section className="mt-12">
                  <div className="mb-6">
                    <h2
                      className={ARTICLE_TITLE_CLASS_NAME}
                      style={ARTICLE_TITLE_STYLE}
                    >
                      {moreArticlesCopy.title}
                    </h2>
                    <p className={ARTICLE_DESCRIPTION_CLASS_NAME}>
                      {moreArticlesCopy.description}
                    </p>
                  </div>

                  <div className="grid gap-6 md:grid-cols-3">
                    {moreArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={getBlogArticlePath(article, currentLanguage)}
                        className="group block h-full"
                      >
                        <Card className="h-full bg-white/95 backdrop-blur-sm shadow-xl transition-shadow group-hover:shadow-2xl">
                          <CardContent className="flex h-full flex-col p-6">
                            {article.coverImage && (
                              <div className="mb-5 overflow-hidden rounded-xl border border-gray-200">
                                <img
                                  src={article.coverImage}
                                  alt={article.title}
                                  className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                  loading="lazy"
                                />
                              </div>
                            )}

                            <h3 className="text-xl font-semibold text-gray-900">
                              {article.title}
                            </h3>

                            <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-700 sm:text-base">
                              {article.excerpt}
                            </p>

                            <div className="mt-5 flex items-center text-sm font-medium text-purple-600 transition-colors group-hover:text-purple-700">
                              <span>{blog.readMore}</span>
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
          <Footer onShowBlog={() => navigate(buildUrl("/blog"))} />
        </div>
      </>
    );
  }

  return (
      <>
        <SEOHead
          overrideSeo={{
          title: blogLandingSeoTitle,
          description: blogLandingSeoDescription,
          keywords: "vevi monster blog, ai dubbing blog, video localization blog, voice cloning, video dubbing, ai voice over",
        }}
        canonicalPathOverride={buildUrl("/blog")}
        canonicalPathIsLocalized={true}
      />
      <Header />
      <div className="min-h-screen vevi-site-background" style={{ paddingTop: "112px" }}>
        <div className="vevi-page-grid">
          <div className="vevi-page-main py-6 sm:py-8 lg:py-10">
            {renderBlogHeroSection({
              title: blogLandingTitle,
              description: blogLandingDescription,
            })}

            <div className="mt-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={getBlogArticlePath(article, currentLanguage)}
                  className="block mb-12 last:mb-0 group"
                >
                  <Card className="bg-white/95 backdrop-blur-sm shadow-xl group-hover:shadow-2xl transition-shadow">
                    <CardContent className="p-8">
                      <div className="flex flex-col md:flex-row items-start gap-6">
                        {article.coverImage && (
                          <img
                            src={article.coverImage}
                            alt={article.title}
                            className="w-full md:w-72 h-44 object-cover rounded-xl border border-gray-200 shrink-0"
                            loading="lazy"
                          />
                        )}
                        <div className="flex-1">
                          <h2 className="text-gray-900 text-2xl mb-3 font-semibold">
                            {article.title}
                          </h2>

                          {article.metadata && Object.keys(article.metadata).length > 0 && (
                            <div className="mb-4 space-y-1">
                              {Object.entries(article.metadata).slice(0, 3).map(([key, value]) => (
                                <div key={key} className="text-sm text-gray-600">
                                  <strong className="text-gray-800">{key}:</strong> {value}
                                </div>
                              ))}
                            </div>
                          )}

                          <p className="text-gray-700 leading-relaxed mb-4">
                            {article.excerpt}
                          </p>

                          <div className="flex items-center text-purple-600 group-hover:text-purple-700 transition-colors">
                            <span className="text-sm font-medium">{blog.readMore}</span>
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <Footer onShowBlog={() => navigate(buildUrl("/blog"))} />
      </div>
    </>
  );
}
